import type { LedgerEntry, PlanNode, WorkItemView } from '../api/types'
import type { LifecycleTone } from './craidLifecycle'
import { isTerminalStanding, standingLabel, standingTone } from './craidLifecycle'
import { byDueAscending, dueTone } from './workRank'
import { addDays, todayIn, usePersonZone, weekdayOf } from '../lib/personTime'
import { dayWords } from './dayWords'
import { openOnCardClick } from './cardClick'

// The row's date words live in `workRank.ts`, a pure module Node runs for the
// parity check. Today's rank itself is the server's since ADR-0050. Re-exported
// here so no caller had to change.
export {
  byDueAscending,
  daysSince,
  dueTone,
  outcomeBehind,
} from './workRank'

/*
  ONE COLUMN ANATOMY, ACROSS SCOPES (ds-promotion/011, ratified 3 Aug, Jesse).

  Seven columns, same order, both scopes: Type · Record · Status · Severity ·
  Owner · Assignee · Due. (Display labels only: phase-1 feedback "Clarify
  everyday labels" renamed Standing to Status and Party to Owner; the fields,
  sort keys and code names are unchanged.) The rule's own words are the reason this file exists
  rather than two row renderers: the two tables must "read as one table at two
  scopes, not two designs".

  It is also G-37's component, arriving in the wave that would otherwise prove
  G-37 right. The register says the work item has "three independent renderings
  and no component", and names them: Plan's work-underneath cards, Work's Today
  cards, Work's Mine rows. This wave builds two of the three. Authoring them
  separately is how the entry's prediction comes true, so they are authored
  here once. This is NOT G-37's design-system promotion, which is a design-lane
  act. That act was blocked on the canvas ratification (G-31's open half) and is
  no longer: ruled 2026-09-08 as 1e for shell chat and 1f for the work item's
  centre of gravity. The promotion is still design-lane work, not this file's.

  A cell with no value for that row's kind shows a dash and the column stays.
  Severity on a work item and party on a personal item are the examples 011
  gives; the point is that the columns line up between the two scopes.
*/

/**
 * The empty cell.
 *
 * 011 writes this as an EM dash, and the em dash is banned in system speech
 * (`jedi-voice-core.md`, "no em dashes, ever") with `tools/language-check`
 * failing CI on the character. Two ratified sources, one glyph, and the
 * conflict is real rather than a reading error.
 *
 * Ruled by Erik 2026-09-04 as the EN dash, then corrected the same day to the
 * PLAIN HYPHEN when the earlier decision surfaced: the wave-10 em-dash sweep
 * already ruled this exact case, and `DECISIONS-LOG.md` records it as "an
 * empty-value dash -> plain hyphen, since the display grammar blesses only `·`
 * and `→`". `RecordPanel` has shipped `-` for empty values since. The en dash
 * is no more blessed than the em dash was, so choosing it would have created a
 * second convention for one thing, which is the drift this component exists to
 * stop. The options put to Erik omitted the hyphen because the prior ruling had
 * not been found yet.
 */
export const EMPTY_CELL = '-'

/** The seven, in the ratified order. Rendered from this list so a header and a row cannot drift. */
export const WORK_COLUMNS = [
  'Type',
  'Record',
  'Status',
  'Severity',
  'Owner',
  'Assignee',
  'Due',
] as const

/**
 * What a row renders, whatever it came from. Both a C·R·A·I·D record and a
 * work item normalize into this, which is what lets one component serve both
 * scopes without either kind leaking its own field names into the table.
 */
export type WorkEntry = {
  key: string
  /** The type chip's letter. Work items take W; records take their C·R·A·I·D letter. */
  chip: string
  /** The short reference, formatted — `R-04`. Null where the record predates the numbering, or is not a C·R·A·I·D record. */
  reference?: string | null
  /** Distinguishes the chip's colour class, never the columns. */
  kind: 'record' | 'item' | 'task'
  /**
   * The FAMILY the chip's ink comes from, where the kind alone does not say it.
   *
   * A work item and a task each have one colour; the five C·R·A·I·D letters do
   * not, and they were all drawn in one neutral grey until wave 24. The token
   * set has kept a `--ledger-*` ink per type all along, which is the design
   * saying these are five things and not one. Absent falls back to the kind,
   * so an unrecognised type keeps the neutral chip rather than borrowing a
   * meaning from whichever letter it starts with.
   */
  tone?: string | null
  title: string
  /** The subject's NAME, never its ref. Null when the item is standalone (G-27). */
  sub: string | null
  /**
   * Where it came from, in words (G-45).
   *
   * Kept ON THE ROW rather than moved to the entry detail, which is where 011
   * sends the other facets. G-45 and wave 10's first exit criterion are both
   * explicit that the ROW is where this belongs: without it "I decided to do
   * this" and "we promised the customer this" look identical, "which is exactly
   * the difference that should make it hard to drop quietly". The entry detail
   * does not render it today, so moving it there would delete a ratified,
   * shipped property rather than relocate it.
   */
  origin: string | null
  standing: string | null
  /** The pill's tone for a C·R·A·I·D standing. Null on work items and tasks, which carry no Ledger lifecycle. */
  standingTone?: LifecycleTone | null
  severity: string | null
  party: string | null
  assignee: string | null
  /** ISO date, or null. The column is DUE, never updated and never age (011 rule 1). */
  due: string | null
  /** Closed rows go quiet (011 rule 1). */
  closed: boolean
  /**
   * The outcome this row traces to reads at risk or off track.
   *
   * This is what "drift" means in phase 1, and it is narrower than the design's
   * fixture prose, deliberately. `Work.dc.html` writes drift as a MEASURE
   * moving ("71% against an 85% target, flat three weeks"), and nothing records
   * an indicator's value, so that reading is not producible. The Outcomes
   * surface settles it from the design side: "not a monitoring dashboard in
   * phase 1", "drift and risk read from linked records, no computed score".
   * So drift here is the computed state of the outcome the work feeds.
   */
  drifting: boolean
  /** Days since anything happened on this row, or null when it has never been touched or is not a work item. */
  staleDays: number | null
}

/**
 * An entry on Today drawn from what the SERVER sent, and nothing else.
 *
 * Today's rank is the server's (ADR-0050), and the screen joins each entry back
 * to the record it points at to draw the richer row. That join can miss: the PM
 * view shows people whose work this browser never loaded. Missing it silently
 * would be the worst of the three outcomes — the server says a thing needs
 * somebody today and the screen quietly drops it — so the card is drawn from
 * the entry itself, with the fields nothing local can supply left empty.
 */
export function entryFromTodayEntry(
  entry: { key: string; kind: string; title: string; reference?: string | null; due?: string | null },
): WorkEntry {
  return {
    key: entry.key,
    chip: entry.kind === 'work_item' ? 'W' : entry.kind === 'task' ? 'T' : entry.kind.slice(0, 1).toUpperCase(),
    kind: entry.kind === 'work_item' ? 'item' : entry.kind === 'task' ? 'task' : 'record',
    // Today's server rows name the family directly, so the chip takes its ink
    // from the same word the row was built from.
    tone: entry.kind === 'work_item' || entry.kind === 'task' ? null : entry.kind.toLowerCase(),
    reference: entry.reference ?? null,
    title: entry.title,
    sub: null,
    origin: null,
    standing: null,
    severity: null,
    party: null,
    assignee: null,
    due: entry.due ?? null,
    closed: false,
    drifting: false,
    staleDays: null,
  }
}

/**
 * A C·R·A·I·D record as a row.
 *
 * `nameOfPerson` resolves the record's `owned_by` edge against the directory,
 * for the Owner column. Without one the column shows the dash, never the side.
 */
export function entryFromRecord(
  entry: LedgerEntry,
  nameOfPerson: (stakeholderId: string) => string | null = () => null,
): WorkEntry {
  const ownerId = (entry.links ?? []).find((l) => (l.linkType ?? '').toLowerCase() === 'owned_by')?.toId ?? null
  return {
    key: entry.id,
    chip: (entry.type ?? '?').slice(0, 1).toUpperCase(),
    kind: 'record',
    tone: entry.type?.toLowerCase() ?? null,
    // `R-04`. The LETTER was already derived here and the number is what was
    // missing, which is why this is one line rather than a module: the chip and
    // the reference are the same fact at two lengths. Null on a record born
    // before the numbering, and null renders as nothing rather than as `R-` —
    // a reference nobody can cite is worse than none.
    reference: entry.reference == null
      ? null
      : `${(entry.type ?? '?').slice(0, 1).toUpperCase()}-${String(entry.reference).padStart(2, '0')}`,
    title: entry.title,
    sub: null,
    // Records have no origin field in the contract; G-45 is a work-item rule.
    origin: null,
    // The GOVERNED label, never the schema value: `wont_fix` and `aged_out`
    // are not English and this column is read by a PM.
    standing: standingLabel(entry.type, entry.lifecycleStatus),
    standingTone: standingTone(entry.type, entry.lifecycleStatus),
    severity: entry.severity ?? null,
    /*
      THE OWNER COLUMN NAMES THE PERSON ACCOUNTABLE, not the side (Erik's
      demo-environment rulings, 2026-10-22). It showed `entry.party`, so a
      risk read "Intact" or "customer" under Owner, which answers who-owns at
      the level of a company; the column's name promises a person, and a
      record's person is its `owned_by` edge (ADR-0044). This departs from
      011's "party" column for records, deliberately: the side is still on the
      record's drawer, in its flags, and a record nobody is accountable for
      shows the dash.
    */
    party: ownerId ? nameOfPerson(ownerId) : null,
    // Records carry no assignee in the contract. The column stays and shows
    // the dash, which is exactly the case 011 legislates for.
    assignee: null,
    // The ledger contract carries no date field at all. Null renders the dash
    // rather than the surface inventing one, and 011 anticipated it: "C·R·A·I·D
    // records without dates show a dash".
    due: null,
    // Per-TYPE, because a Ledger close is type-specific: `accepted` closes a
    // risk and `active` opens a decision. Agrees with `LedgerFlows.IsTerminal`
    // by construction — see `craidLifecycle.ts`.
    closed: isTerminalStanding(entry.type, entry.lifecycleStatus),
    // A record is not work, so neither band applies to it. Today reads work
    // items; these rows reach the other lenses.
    drifting: false,
    staleDays: null,
  }
}

/** A work item as a row. */
export function entryFromWorkItem(
  item: WorkItemView,
  subject: string | null,
  nameOf: NameLookup = () => null,
  signals?: WorkSignals,
): WorkEntry {
  return {
    key: item.id,
    chip: 'W',
    kind: 'item',
    title: item.title,
    sub: subject,
    // Origin in words (G-45). A ref would look like information and answer no
    // question a person has, which is why the API sends words or nothing. Both
    // this and the subject render: an item can have a subject AND have been
    // promised to a customer, and collapsing them loses the second.
    origin: item.origin ?? null,
    standing: item.state ?? null,
    // 011's own example of a cell with no value for that row's kind.
    severity: null,
    party: item.recipient ?? null,
    assignee: assignmentOf(item, nameOf),
    // effectiveDue, not due: the item's own date when it has one, inherited
    // from its subject when it does not. The API already does that join, so
    // the surface reads the answer rather than recomputing it.
    due: item.effectiveDue ?? item.due ?? null,
    closed: item.resolved,
    drifting: signals?.drifting ?? false,
    staleDays: signals?.staleDays ?? null,
  }
}

/**
 * What surveillance and the clock say about one row. Passed in rather than
 * computed here, because both need data this function does not have: the plan
 * graph to walk work to its outcome, and the watch to read that outcome's
 * state.
 */
export type WorkSignals = { drifting: boolean; staleDays: number | null }

/**
 * Who an item is assigned to, by name.
 *
 * `assignees` holds Entra object ids. Rendering one raw would put a GUID on
 * screen, which is on the language-enforcement list and which wave 10 already
 * had to strip out of two spans, so this resolves through engagement
 * membership instead.
 *
 * Naming a person here is permitted rather than merely tolerated. C-034 allows
 * an answer that names an individual "PROVIDED the answer is derived from plan
 * and record data alone", and lists ASSIGNMENT among its own examples. What it
 * forbids is joining team-member observation data to an individual, which a
 * name on a work item does not do.
 *
 * Two honest fallbacks rather than one confident guess:
 * - an object id with no membership behind it says the count, because the
 *   alternative is either a GUID or a silent omission of somebody real;
 * - more than one assignee says the first plus how many others, because the
 *   column is one line and a list would set the row's height by its busiest row.
 */
export type NameLookup = (objectId: string) => string | null

function assignmentOf(item: WorkItemView, nameOf: NameLookup): string | null {
  const ids = item.assignees ?? []
  if (ids.length === 0) return null
  const names = ids.map((id) => nameOf(id)).filter((n): n is string => !!n)
  if (names.length === 0) {
    return ids.length === 1 ? 'Assigned' : `Assigned to ${ids.length}`
  }
  const [first, ...rest] = names
  const unresolved = ids.length - names.length
  const others = rest.length + unresolved
  return others === 0 ? first! : `${first} and ${others} more`
}

/*
 * The TASK vocabulary, and only that, as of 2026-09-11.
 *
 * This set is `taskStanding`'s, reading a plan node's `state`. It used to have a
 * second caller — `entryFromRecord` — and that was the defect: the set describes
 * work-item and task states (`done` and `dismissed` are ADR-0031 Amendment §1's;
 * `closed` and `promoted` are a task's), and it was deciding whether a C·R·A·I·D
 * RECORD was closed. Only `resolved` and `accepted` overlap the Ledger's twelve
 * terminal statuses, and they overlap by coincidence — so a cleared concern, an
 * averted or materialized risk, a validated or invalidated assumption, a
 * won't-fix issue and a superseded, reversed or aged-out decision all read as
 * live work: undimmed, through the active filter, still carrying urgency rank.
 *
 * Records now read `isTerminalStanding` from `craidLifecycle.ts`, which is
 * per-type and agrees with `LedgerFlows.IsTerminal` by construction. Work items
 * are unchanged and never used this set — they read `item.resolved`, server-side.
 *
 * One function answering the same question for two vocabularies is the shape to
 * watch for here; it stayed invisible because no fixture carried a closed record.
 */
const CLOSED_STANDINGS = new Set(['closed', 'done', 'dismissed', 'resolved', 'promoted', 'accepted'])



/**
 * SORTING, WHICH IS WHAT MAKES A TABLE ONE (the design's `MINE_SORTS` and
 * `LG_SORTS`). Every column sorts, each with the direction that is useful
 * first: names and dates ascending, severity descending, because nobody asks
 * for the least severe thing first.
 *
 * Clicking the column being sorted flips it; clicking another jumps to that
 * column's own default. The tiebreak is always the due date, so two rows that
 * a column cannot tell apart still come out in the order the day cares about.
 */
export type SortKey = 'type' | 'record' | 'standing' | 'severity' | 'party' | 'assignee' | 'due'
export type SortDir = 'asc' | 'desc'

const SORT_KEYS: Record<(typeof WORK_COLUMNS)[number], SortKey> = {
  Type: 'type', Record: 'record', Status: 'standing', Severity: 'severity',
  Owner: 'party', Assignee: 'assignee', Due: 'due',
}

/** The direction each column opens in. */
export const SORT_DEFAULT: Record<SortKey, SortDir> = {
  type: 'asc', record: 'asc', standing: 'asc', severity: 'desc',
  party: 'asc', assignee: 'asc', due: 'asc',
}

// The design's own ranks: the C·R·A·I·D alphabet first, then tasks, then work.
const TYPE_RANK: Record<string, number> = { C: 0, R: 1, A: 2, I: 3, D: 4, T: 8, W: 9 }
const SEVERITY_RANK: Record<string, number> = { low: 0, moderate: 1, high: 2, critical: 3 }
const PARTY_RANK: Record<string, number> = { intact: 0, customer: 1, joint: 2, 'third-party': 3 }
/** Sorts last, whichever way round the column is: an absent value answers no question. */
const LAST = '\uffff'

const sortValue = (entry: WorkEntry, key: SortKey): string | number => {
  switch (key) {
    case 'type': return TYPE_RANK[entry.chip] ?? 99
    case 'record': return entry.title.toLowerCase()
    // Closed rows sort under open ones whichever standing they carry: 011's
    // "closed rows go quiet" said in the sort as well as in the colour.
    case 'standing': return `${entry.closed ? 1 : 0}·${(entry.standing ?? '').toLowerCase()}`
    case 'severity': return SEVERITY_RANK[(entry.severity ?? '').toLowerCase()] ?? -1
    // A side in its rank, then a person by name (a record's Owner), then the empty cell.
    case 'party': {
      const owner = (entry.party ?? '').trim().toLowerCase()
      if (!owner) return LAST
      return owner in PARTY_RANK ? `0${PARTY_RANK[owner]}` : `1${owner}`
    }
    case 'assignee': return (entry.assignee ?? LAST).toLowerCase()
    case 'due': return entry.due ?? LAST
  }
}

export function sortRows<T extends { entry: WorkEntry }>(rows: readonly T[], key: SortKey, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const left = sortValue(a.entry, key)
    const right = sortValue(b.entry, key)
    if (left < right) return -1 * sign
    if (left > right) return 1 * sign
    return byDueAscending(a.entry, b.entry)
  })
}

export function WorkColumnHeader({
  sort,
  onSort,
}: {
  /** The column the table is sorted by, and which way. Absent leaves the header a label. */
  sort?: { key: SortKey; dir: SortDir }
  onSort?: (key: SortKey) => void
} = {}) {
  return (
    <li className="work-cols work-cols-head" aria-hidden={onSort ? undefined : 'true'}>
      {WORK_COLUMNS.map((column) => {
        const key = SORT_KEYS[column]
        const on = sort?.key === key
        if (!onSort) {
          return <span key={column} className="work-col-label">{column}</span>
        }
        return (
          <button
            key={column}
            type="button"
            className={on ? 'work-col-label work-col-sorted' : 'work-col-label work-col-sortable'}
            aria-sort={on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
            title={`Sort by ${column.toLowerCase()}`}
            onClick={() => onSort(key)}
          >
            {column}
            {on && <span className={`work-col-arrow work-col-${sort!.dir}`} aria-hidden="true" />}
          </button>
        )
      })}
    </li>
  )
}

/**
 * One row, seven cells, whatever it came from.
 *
 * `children` is the row's actions. They sit outside the seven columns on
 * purpose: 011 governs the column anatomy, and an eighth column of buttons
 * would be the "extra columns in one scope only" it rules out.
 */
export function WorkEntryRow({
  entry,
  onOpen,
  selected,
  anchorId,
  children,
}: {
  entry: WorkEntry
  onOpen?: () => void
  selected?: boolean
  anchorId?: string
  children?: React.ReactNode
}) {
  const tone = dueTone(entry, usePersonZone())
  const classes = [
    'work-cols',
    'work-entry',
    // The WHOLE row opens (Erik, 2026-09-23), so the whole row says so.
    onOpen ? 'work-entry-open' : '',
    selected ? 'work-entry-selected' : '',
    entry.closed ? 'work-entry-quiet' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <li id={anchorId} className={classes} onClick={openOnCardClick(onOpen)}>
      <span className={`work-chip work-chip-${entry.tone ?? entry.kind}`}>{entry.chip}</span>

      <span className="work-cell-record">
        {/*
          The reference, ahead of the title. `jedi-writing.md` governs where it
          may go: "Record ids are chips, citations and trailing qualifiers. They
          are never the grammatical subject and never the first word." A table
          CELL is the chip case rather than the sentence case - it is a label
          beside the record, not a clause about it - which is what the design
          renders too (`W-118 · Baseline the requirements`).
        */}
        {entry.reference && <span className="work-cell-ref">{entry.reference}</span>}
        {onOpen ? (
          <button type="button" className="work-cell-title work-cell-open" onClick={onOpen}>
            {entry.title}
          </button>
        ) : (
          <span className="work-cell-title">{entry.title}</span>
        )}
        {(entry.sub || entry.origin) && (
          <span className="work-cell-sub">
            {entry.sub}
            {entry.sub && entry.origin && <span className="work-cell-dot">·</span>}
            {entry.origin && <span className="work-cell-origin">{entry.origin}</span>}
          </span>
        )}
      </span>

      <StandingCell entry={entry} />
      <Cell value={entry.severity} />
      <Cell value={entry.party} />
      <Cell value={entry.assignee} />
      <span className={tone ? `work-cell-due work-due-${tone}` : 'work-cell-due'}>
        {entry.due ?? EMPTY_CELL}
      </span>

      {children && <span className="work-entry-actions">{children}</span>}
    </li>
  )
}

/**
 * The standing column.
 *
 * A C·R·A·I·D record gets the design system's LifecyclePill treatment — a dot
 * plus a text label, read together. `LifecyclePill.jsx` is explicit that it
 * "never encodes by color alone", so the label carries the meaning and the dot
 * is reinforcement, which is also what keeps this readable for anyone who does
 * not separate the tones.
 *
 * Work items and tasks keep the plain cell: their standing is a different
 * vocabulary on a different axis, and giving it a Ledger pill would assert a
 * kinship the model does not have.
 */
function StandingCell({ entry }: { entry: WorkEntry }) {
  if (entry.kind !== 'record' || !entry.standing) {
    return <Cell value={entry.standing} className="work-cell-standing" />
  }
  return (
    <span className="work-cell-standing">
      <span className={`lifecycle-pill lifecycle-${entry.standingTone ?? 'unknown'}`}>
        <span className="lifecycle-dot" aria-hidden="true" />
        {entry.standing}
      </span>
    </span>
  )
}

/** A cell that holds its column open when it has nothing to say (011). */
function Cell({ value, className }: { value: string | null; className?: string }) {
  const empty = value === null || value === ''
  return (
    <span className={[className, empty ? 'work-cell-empty' : ''].filter(Boolean).join(' ')}>
      {empty ? EMPTY_CELL : value}
    </span>
  )
}


/**
 * The four bands, in the design's words. The rule line is not decoration: 008's
 * sibling principle applies here too, that a ranked list which does not say why
 * it ranked is a list a person has to trust rather than read.
 */
export const BANDS: Record<1 | 2 | 3 | 4, { label: string; rule: string }> = {
  1: {
    label: 'Drifting and urgent',
    rule: 'the outcome is moving away from target and the date has passed. Already costing you',
  },
  2: {
    // The design's own rule line, restored in the wave that made it true: the
    // band holds stopped and never-planned work too now (ADR-0050), and a rule
    // naming two of its four reasons is a rubric that mis-states itself.
    label: 'Urgent',
    rule: 'overdue, due today, stopped, or never planned. People and sentiment are waiting on it',
  },
  3: {
    label: 'Drifting, not urgent yet',
    rule: 'the outcome is moving · act here so it never becomes overdue',
  },
  4: {
    label: 'Stale',
    rule: 'untouched past the threshold · no date pressure, no drift',
  },
}



/*
  THE T LENS (ds-promotion/009, ratified 3 Aug, Jesse).

  Three guardrails come with the ruling and all three are load-bearing here:

  1. T is a LENS ENTRY, not a ledger type. The alphabet stays C·R·A·I·D. The
     chip is the same square-letter form as the local W chip and never the
     TypeChip component, so nothing here touches the ledger vocabulary.
  2. Task standing on Work is DERIVED from the task's work items and read-only.
     "Work never becomes a second place to enter task status." So a T row
     carries no actions at all, and completion stays owner-asserted on the plan.
  3. One owner per task, read back from the plan. Assignment happens on work
     items, never from this lens.

  The rationale is role-extensibility rather than tidiness: C·R·A·I·D is
  PM-shaped, and a developer or tester's day is tasks. Without T, Work is a
  surface only a PM can use.
*/

/** The work items sitting under a task. Their subject ref is how the link is expressed. */
export function itemsUnderTask(taskId: string, items: readonly WorkItemView[]): WorkItemView[] {
  return items.filter((i) => i.subjectRef === `task:${taskId}`)
}

/**
 * Task standing, derived (009 guardrail 2). The four readings are the design's
 * own, and each says what to DO rather than naming a state:
 *
 *   no work items       Unplanned      nothing moves until it is broken down
 *   all resolved        Awaiting close the close is the owner's alone
 *   some open           In progress
 *   task already closed Closed
 */
export function taskStanding(node: PlanNode, under: readonly WorkItemView[]): string {
  if (node.state && CLOSED_STANDINGS.has(node.state.toLowerCase())) return 'Closed'
  if (under.length === 0) return 'Unplanned'
  return under.every((i) => i.resolved) ? 'Awaiting close' : 'In progress'
}

/** The count line under a task's name. Counts inform; they never derive completion (G-30). */
function taskWorkLine(under: readonly WorkItemView[]): string {
  if (under.length === 0) return 'No work items yet. Nothing moves until it is broken down.'
  const open = under.filter((i) => !i.resolved).length
  if (open === 0) return 'All work items resolved. The close is yours.'
  return `${open} of ${under.length} work items open`
}

/**
 * A task as a row.
 *
 * `owner` is read back from the plan and rendered in the Assignee column,
 * which is the one place a real name appears there today: a task owner is a
 * plan field holding a person's name, not an object id, so it does not hit the
 * resolution gap that makes work-item assignees say "Assigned".
 */
export function entryFromTask(node: PlanNode, under: readonly WorkItemView[]): WorkEntry {
  const standing = taskStanding(node, under)
  return {
    key: node.id,
    chip: 'T',
    kind: 'task',
    title: node.name ?? '(untitled task)',
    sub: taskWorkLine(under),
    origin: null,
    standing,
    // Severity is a ledger facet. A task has none, and 011 says the column
    // stays and shows the dash rather than disappearing for this row's kind.
    severity: null,
    // Who is accountable (ADR-0030's 2026-10-20 amendment). This column read the
    // task's audience until then, which is who SEES it, never who owns it.
    party: node.party ?? null,
    assignee: node.owner ?? null,
    due: node.expectedEnd ?? node.date ?? null,
    closed: standing === 'Closed',
    // A task's standing is DERIVED from the work beneath it (009's guardrail),
    // so it carries neither signal of its own. When a task is on Today, the
    // server says why (ADR-0050).
    drifting: false,
    staleDays: null,
  }
}

/**
 * Is this task the signed-in person's?
 *
 * Ownership is not the only answer, and §8a is explicit about why: "a work item
 * can be held without owning its task (assignment is item-level; task assignees
 * read back)". Being handed an item under a task is how you join that task, so
 * a task counts as yours if you own it OR you hold work beneath it. Reading
 * ownership alone would hide the tasks somebody is actually working on.
 */
export function isMyTask(
  node: PlanNode, under: readonly WorkItemView[], me: string | null,
  myNames: readonly (string | null | undefined)[] = [],
): boolean {
  if (!me) return false
  // A task owner is a NAME (see entryFromTask), so it is matched by the
  // person's names: the one they chose, and the one they signed in with, so a
  // rename never orphans what they took before it. Compared with the object id
  // alone, as it was until wave 23, an owned task was never anyone's.
  if (node.owner && (node.owner === me || myNames.some((n) => sameName(node.owner!, n)))) return true
  return under.some((i) => (i.assignees ?? []).includes(me))
}

const sameName = (a: string, b?: string | null) =>
  !!b && a.trim().toLowerCase() === b.trim().toLowerCase()

/*
  TIMELINE (Work.dc.html: "my work by due date, pull anything forward
  yourself"; its hint is "what is coming, so a light today is not wasted").

  Personal scope, like Today and Mine. Three bands in the design: This week,
  Next week, Later.

  A FOURTH GROUP, departing from the design deliberately. The reference
  implementation sorts undated work with `due || 999`, which lands it in
  "Later". Later is a claim about WHEN, and an item with no date supports no
  such claim: on a surface whose whole column rule is that an absent value
  shows a dash rather than being invented, quietly filing undated work under a
  date range is the same error in a different place. It gets its own group and
  says what it is.
*/
/**
 * A column on Timeline.
 *
 * `range` and `tone` are the design's (`Work.dc.html`'s `WEEK`): a caption
 * saying which days the column covers, and the colour its heading is set in.
 *
 * The per-column hint line this type used to carry is gone. It was written when
 * the groups were stacked tables with nothing else to say what each one held;
 * beside a date range, in a column, it was a second caption saying the same
 * thing, and it made the columns start at four different heights.
 */
export type TimelineGroup = {
  label: string
  /** Which days this column covers, in words: "to 27 Sep", "28 Sep to 4 Oct", "5 Oct on". */
  range: string
  /** 1 this week · 2 next week · 3 later · 4 no date, for the heading's colour. */
  tone: 1 | 2 | 3 | 4
  entries: WorkEntry[]
}

export function timelineGroups(entries: readonly WorkEntry[], zone: string, today = new Date()): TimelineGroup[] {
  // Sunday-ending week, matching the design's "This week / Next week" split,
  // counted from the person's day (RAID #482) rather than the UTC one.
  const day = todayIn(zone, today)
  const thisWeek = addDays(day, 7 - (weekdayOf(day) || 7))
  const next = addDays(thisWeek, 7)

  const dated = entries.filter((e) => e.due).sort(byDueAscending)
  const undated = entries.filter((e) => !e.due).sort(byDueAscending)

  /*
    THE THREE DATED COLUMNS ALWAYS STAND (the design's own: it draws all three
    and writes "Nothing due." under an empty one). A column that disappears when
    it empties makes the week's shape change under the reader, and the empty one
    is information: nothing is due next week.

    The fourth is ours and is dropped when empty, because "Nothing due" under
    "No date yet" would be answering a question nobody asked. The design has no
    such column — every item in its fixture carries a date.
  */
  const columns: TimelineGroup[] = [
    {
      label: 'This week',
      range: `to ${dayWords(thisWeek, today)}`,
      tone: 1,
      entries: dated.filter((e) => e.due! <= thisWeek),
    },
    {
      label: 'Next week',
      range: `${dayWords(addDays(thisWeek, 1), today)} to ${dayWords(next, today)}`,
      tone: 2,
      entries: dated.filter((e) => e.due! > thisWeek && e.due! <= next),
    },
    {
      label: 'Later',
      range: `${dayWords(addDays(next, 1), today)} on`,
      tone: 3,
      entries: dated.filter((e) => e.due! > next),
    },
  ]
  return undated.length === 0
    ? columns
    : [...columns, {
        label: 'No date yet',
        range: 'no date',
        tone: 4,
        entries: undated,
      }]
}


/*
  UNASSIGNED WORK (G-28), and what the entry actually asks for.

  The register: "Unassigned and unattached work must surface somewhere. Work's
  log flags it orange but nothing routes it." Its open sub-question, "does
  accepting a candidate force an assignee?", was ruled by Erik on 2026-09-04:
  unassigned is fine for now. So this is a SURFACING job, not a prevention one,
  and the orphan pile stays legitimate exactly as wave 10 accepted it.

  Surfacing means the row says so where somebody is already looking, rather
  than a separate list nobody opens. The Assignee column is that place: it now
  carries a name when there is one, so its absence is the signal.
*/
export function isUnassigned(entry: WorkEntry): boolean {
  return entry.kind === 'item' && !entry.closed && entry.assignee === null
}

/*
  THE CLEARED DAY (G-41).

  "No cold start on any of the four lenses, and nothing marks the day as
  cleared. The stated first principle was: bang it out and get the reward at
  the end."

  Two states that look alike and are not. The design keeps them apart and the
  difference is the entire point:

    cleared      there WERE items today and none are open now
                 "All N items are done or moved with a reason. That was the
                 day. The blank space below is the reward."

    never busy   nothing was due in the first place
                 "Today's list is clear. Nothing else is overdue, due, stopped,
                 or stale."

  Collapsing them into one empty state throws away the reward, which is the
  only thing G-41 is actually about. A quiet day and a finished day should not
  read the same.
*/
export const CLEARED_LABEL = 'Day cleared'

export function clearedLine(count: number): string {
  const items = count === 1 ? '1 item is' : `All ${count} items are`
  return `${items} done or moved with a reason. That was the day.`
}

/**
 * The label over Today's assertion, in the design's words, with its own
 * tooltip: what is on the list is COMPUTED from dates and record state, never
 * proposed. 013's display grammar reserves this label for exactly that.
 */
export const ASSERTS_LABEL = 'J.E.D.I. asserts'
export const ASSERTS_BASIS =
  'Computed from dates and record state, cited on each card. Nothing here is a proposal.'

/**
 * What Today says about itself, above the bands.
 *
 * The design's sentence, with two changes the wave made necessary. Its "N items
 * today. First is W-50: …" quotes a work item's reference, and Today now holds
 * kinds that carry none, so a task is quoted by its name instead. And what is
 * waiting on Home is counted here, ruled by Erik on 2026-09-22 in the same
 * breath as keeping approvals and intake questions ON Home: Today says how much
 * is there, and Home is still where it is done.
 *
 * Only the first character of the reason is lowered. The design lowercases the
 * whole clause, which would also lower the outcome names inside it.
 */
export function assertsLine(
  first: { reference?: string | null; title: string; why: string[] } | null,
  count: number,
  waiting: { approvals: number; questions: number },
): string {
  const held = [
    waiting.approvals > 0
      ? `${waiting.approvals === 1 ? '1 proposed change' : `${waiting.approvals} proposed changes`}`
      : null,
    waiting.questions > 0
      ? `${waiting.questions === 1 ? '1 question from intake' : `${waiting.questions} questions from intake`}`
      : null,
  ].filter(Boolean)
  const onHome = held.length === 0
    ? ''
    : ` ${held.join(' and ')} ${held.length === 1 && waiting.approvals + waiting.questions === 1 ? 'is' : 'are'} waiting on Home.`

  if (first === null || count === 0) {
    // Says what was checked, so the silence is legible. The design's "pull
    // something ahead" is not offered: pulling ahead is not built.
    return `Today is clear. Nothing of yours is overdue, due today, stopped, drifting or gone quiet.${onHome}`
  }
  const names = first.reference ?? `'${first.title}'`
  const because = first.why[0] ?? 'it needs you today'
  return `${count === 1 ? '1 item' : `${count} items`} today. First is ${names}: `
    + `${because.charAt(0).toLowerCase()}${because.slice(1)}.`
    + ` Nothing here was ordered by a guess at how long it takes.${onHome}`
}
